// ── RECORDING HOW A CLIENT WATCHES A COURSE VIDEO (since 2026-10-01) ─────────
//
// Used by DayVideo.jsx (Days 1–6) and CallDay.jsx (day 7). Both embed Wistia
// the same way; this attaches to that player through Wistia's own queue
// (window._wq) and records, per SITTING, what the client actually did:
//
//   seen      unique seconds actually seen — the masterclass page's rules
//             (free-masterclass/index.html, THE SEEN-TIME MEASURE): a small
//             forward step of playback (0 < step <= 3 s) marks the seconds it
//             crossed; a seek breaks the chain, so skipping ahead marks
//             nothing; a second already marked is never marked again, so
//             rewatching never counts twice
//   pauses    each pause with the video position — not the one at the very
//             end, and not the pause Wistia fires by itself within 750 ms of a
//             play or a jump in position (seen in real test plays: a "pause at
//             0:00" straight after starting, and one on every seek)
//   speed     each speed change, with the new speed and the position (Wistia
//             reports one change twice within ~10 ms; the repeat is dropped)
//   skips     each seek, from → to (a drag across the bar is one skip)
//   finished  the video reached its end
//
// A sitting starts at the first play on a page load. A new sitting starts if
// playback resumes after 30 minutes without any.
//
// SENT IN BATCHES, NEVER STREAMED: every 20 seconds while something new has
// happened, and at once on pause, end, speed change, the tab being hidden,
// leaving the page, or this page unmounting. Each send carries the whole
// sitting with a rising seq (the backend keeps the newest and unions the
// seen seconds), so a lost or repeated send costs nothing.
//
// IT CAN NEVER AFFECT PLAYBACK: every handler is wrapped; sends are
// fire-and-forget (keepalive fetch, errors swallowed, no redirect on 401);
// nothing here is awaited by the page. It does not touch ry_progress — a day
// still counts as complete exactly as before (the Continue button).

const API = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const NEW_SITTING_AFTER_MS = 30 * 60 * 1000;
const FLUSH_EVERY_MS = 20 * 1000;
const MAX_EVENTS = 200;
const MAX_SECONDS = 7200;

const newKey = () => {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, ''); } catch (e) { /* fall through */ }
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};
const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Start recording one course video. Returns a stop() for the page's effect
 * cleanup. Safe to call when Wistia never loads: it simply records nothing.
 */
export function trackCourseVideo(mediaId, dayNum) {
  let video = null;
  let sitting = null;
  let stopped = false;
  let timer = null;
  let lastSeekAt = 0;
  let lastPlayAt = 0;
  const bound = [];

  const startSitting = (posNow) => {
    sitting = {
      key: newKey(), seq: 0, dirty: true,
      seen: [], seenCount: 0, lastT: null,
      startPosition: Math.floor(posNow || 0), endPosition: Math.floor(posNow || 0),
      pauses: [], speedChanges: [], skips: [],
      finished: false, lastActivity: Date.now(), begunAt: performance.now(),
    };
  };

  const ranges = () => {
    const out = [];
    let start = -1;
    const seen = sitting.seen;
    for (let i = 0; i < seen.length; i++) {
      if (seen[i] && start < 0) start = i;
      if (!seen[i] && start >= 0) { out.push([start, i]); start = -1; }
    }
    if (start >= 0) out.push([start, seen.length]);
    return out;
  };

  const send = () => {
    try {
      if (!sitting || !sitting.dirty) return;
      const token = localStorage.getItem('ry_token');
      if (!token) return;
      sitting.dirty = false;
      sitting.seq += 1;
      let duration = 0;
      try { duration = video ? video.duration() : 0; } catch (e) { duration = 0; }
      const body = JSON.stringify({
        dayNum, mediaId, sittingKey: sitting.key, seq: sitting.seq,
        // How long ago this sitting began, by the device's own timer (not its
        // clock, which can be wrong) — the backend dates the sitting from it.
        startedAgoMs: Math.round(performance.now() - sitting.begunAt),
        duration: Math.round(duration || 0),
        startPosition: sitting.startPosition, endPosition: sitting.endPosition,
        seenRanges: ranges(),
        pauses: sitting.pauses, speedChanges: sitting.speedChanges, skips: sitting.skips,
        finished: sitting.finished,
      });
      fetch(`${API}/api/video-watch`, {
        method: 'POST', keepalive: body.length < 60000,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body,
      }).catch(() => { /* recording only — never affects the page */ });
    } catch (e) { /* never throw into the player */ }
  };

  const touch = () => {
    // Resuming after a long gap is a new sitting: close the old one first.
    const now = Date.now();
    if (!sitting) { startSitting(video ? video.time() : 0); return; }
    if (now - sitting.lastActivity > NEW_SITTING_AFTER_MS) {
      sitting.dirty = true; send();
      startSitting(video ? video.time() : 0);
      return;
    }
    sitting.lastActivity = now;
  };

  const on = (event, fn) => {
    const wrapped = (...args) => { try { if (!stopped) fn(...args); } catch (e) { /* never throw into the player */ } };
    video.bind(event, wrapped);
    bound.push([event, wrapped]);
  };

  const attach = (v) => {
    if (stopped) return;
    video = v;
    on('play', () => { touch(); lastPlayAt = Date.now(); sitting.dirty = true; });
    on('timechange', (t) => {
      if (!sitting || typeof t !== 'number' || !isFinite(t)) return;
      if (sitting.lastT !== null) {
        const step = t - sitting.lastT;
        if (step > 0 && step <= 3) {
          for (let i = Math.floor(sitting.lastT); i < Math.floor(t); i++) {
            if (i >= 0 && i < MAX_SECONDS && !sitting.seen[i]) { sitting.seen[i] = true; sitting.seenCount++; sitting.dirty = true; }
          }
        }
      }
      sitting.lastT = t;
      sitting.endPosition = Math.floor(t);
      sitting.lastActivity = Date.now();
    });
    on('seek', (currentTime, lastTime) => {
      if (!sitting) touch();
      lastSeekAt = Date.now();
      sitting.lastT = null;
      const from = Number(lastTime), to = Number(currentTime);
      if (!isFinite(from) || !isFinite(to) || Math.abs(to - from) < 1) return;
      const last = sitting.skips[sitting.skips.length - 1];
      // A drag along the bar fires many seeks in a row — keep it as one skip.
      if (last && Date.now() - last._t < 2000) { last.to = round1(to); last._t = Date.now(); }
      else if (sitting.skips.length < MAX_EVENTS) sitting.skips.push({ at: new Date().toISOString(), from: round1(from), to: round1(to), _t: Date.now() });
      sitting.endPosition = Math.floor(to);
      sitting.dirty = true;
    });
    on('pause', () => {
      if (!sitting) return;
      let p = 0, d = 0;
      try { p = video.time(); d = video.duration(); } catch (e) { /* ignore */ }
      const atEnd = d > 0 && p >= d - 1;
      const byJump = Date.now() - Math.max(lastSeekAt, lastPlayAt) < 750;
      if (!atEnd && !byJump && sitting.pauses.length < MAX_EVENTS) sitting.pauses.push({ at: new Date().toISOString(), pos: round1(p) });
      sitting.dirty = true;
      send();
    });
    on('playbackratechange', (rate) => {
      if (!sitting) touch();
      let p = 0;
      try { p = video.time(); } catch (e) { /* ignore */ }
      const prev = sitting.speedChanges[sitting.speedChanges.length - 1];
      if (prev && prev.rate === Number(rate) && Date.now() - Date.parse(prev.at) < 1000) return;
      if (sitting.speedChanges.length < MAX_EVENTS) sitting.speedChanges.push({ at: new Date().toISOString(), pos: round1(p), rate: Number(rate) });
      sitting.dirty = true;
      send();
    });
    on('end', () => {
      if (!sitting) touch();
      sitting.finished = true;
      sitting.dirty = true;
      send();
    });
  };

  const onHide = () => { if (document.visibilityState === 'hidden') send(); };
  const wq = { id: mediaId, onReady: (v) => { try { attach(v); } catch (e) { /* never throw */ } } };
  try {
    window._wq = window._wq || [];
    window._wq.push(wq);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', send);
    timer = setInterval(send, FLUSH_EVERY_MS);
  } catch (e) { /* recording only */ }

  // (Skips carry a private _t used only to merge a drag into one skip; the
  // backend keeps only at / from / to.)
  return function stop() {
    try {
      send();
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', send);
      if (video) for (const [ev, fn] of bound) { try { video.unbind(ev, fn); } catch (e) { /* ignore */ } }
      try { window._wq.push({ revoke: wq }); } catch (e) { /* ignore */ }
    } catch (e) { /* never throw */ }
  };
}
